import React from 'react';
import { motion } from 'framer-motion';
import { Phone, CheckCircle, Activity, MessageSquare } from 'lucide-react';

const VoIPSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

        <div className="flex flex-row items-center gap-16 max-w-7xl mx-auto z-10 w-full">
            
            {/* Left Content */}
            <div className="flex-1 space-y-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200">
                    <Phone className="w-4 h-4" />
                    <span>Intelligent VoIP</span>
                </div>

                <h2 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight">
                    Modern VoIP Designed <br />
                    <span className="text-blue-600">Exclusively for Dental</span>
                </h2>

                <p className="text-xl text-slate-600 leading-relaxed max-w-xl">
                    Stop using generic phone systems. Our VoIP platform runs natively with Asha PMS after migration, giving you superpowers when the phone rings.
                </p>

                <div className="grid grid-cols-2 gap-4 mt-6">
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                        <div className="text-2xl font-bold text-slate-900 mb-1">150k+</div>
                        <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">Users Nationwide</div>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                        <div className="text-2xl font-bold text-slate-900 mb-1">24/7</div>
                        <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">Expert Support</div>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 flex-shrink-0">
                            <CheckCircle className="w-6 h-6" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900">Patient Pop-Up</h4>
                            <p className="text-slate-500 text-sm">See upcoming appointments, balances, and family members instantly.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                            <Activity className="w-6 h-6" />
                        </div>
                        <div>
                            <h4 className="font-bold text-slate-900">Proven Reliability</h4>
                            <p className="text-slate-500 text-sm">Handles high call volumes with carrier-grade reliability.</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Visual - VoIP Card */}
            <motion.div 
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8 }}
                className="flex-1 w-full max-w-md"
            >
                <div className="relative aspect-[4/5] md:aspect-square">
                    {/* Phone Screen Container */}
                    <div className="absolute inset-0 bg-slate-900 rounded-[2.5rem] shadow-2xl p-4 ring-8 ring-slate-800">
                        {/* Screen Content */}
                        <div className="h-full bg-slate-800 rounded-[1.5rem] overflow-hidden flex flex-col relative">
                            {/* Status Bar */}
                            <div className="h-8 bg-slate-900 w-full flex items-center justify-between px-6">
                                <span className="text-[10px] text-white">9:41</span>
                                <div className="flex gap-1.5">
                                    <div className="w-3 h-3 bg-white rounded-full opacity-20" />
                                    <div className="w-3 h-3 bg-white rounded-full opacity-20" />
                                </div>
                            </div>

                            {/* Incoming Call UI */}
                            <div className="flex-1 flex flex-col items-center pt-12 px-6 relative z-10">
                                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-3xl font-bold text-white shadow-lg mb-6 ring-4 ring-blue-500/20">
                                    SJ
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-1">Sarah Jenkins</h3>
                                <p className="text-blue-400 mb-8 animate-pulse">Incoming Call...</p>

                                {/* Smart Pop-Up Card */}
                                <motion.div 
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    transition={{ delay: 0.5 }}
                                    className="w-full bg-white/10 backdrop-blur-md border border-white/10 rounded-xl p-4 space-y-3"
                                >
                                    <div className="flex items-center gap-2 mb-2">
                                        <Activity className="w-4 h-4 text-blue-400" />
                                        <span className="text-xs font-bold text-blue-100 uppercase tracking-wider">Smart Insights</span>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-slate-900/50 p-2.5 rounded-lg border border-white/5">
                                            <span className="text-[10px] text-slate-400 block mb-1">Next Appt</span>
                                            <span className="text-sm font-semibold text-white">Tue, 2:00 PM</span>
                                        </div>
                                        <div className="bg-slate-900/50 p-2.5 rounded-lg border border-white/5">
                                            <span className="text-[10px] text-slate-400 block mb-1">Balance</span>
                                            <span className="text-sm font-semibold text-red-400">$150.00</span>
                                        </div>
                                        <div className="bg-slate-900/50 p-2.5 rounded-lg border border-white/5 col-span-2">
                                            <span className="text-[10px] text-slate-400 block mb-1">Family Members</span>
                                            <span className="text-sm font-semibold text-white">Mike (Husband), Emma (Child)</span>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Action Buttons */}
                                <div className="mt-auto w-full grid grid-cols-2 gap-4 pb-8 pt-6">
                                    <button className="h-14 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center hover:bg-red-500/30 transition-colors">
                                        <Phone className="w-6 h-6 rotate-[135deg]" />
                                    </button>
                                    <button className="h-14 rounded-full bg-green-500 text-white flex items-center justify-center hover:bg-green-400 transition-colors shadow-lg shadow-green-500/30 animate-bounce">
                                        <Phone className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>
                            
                            {/* Background Pattern */}
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-blue-900/40 via-slate-900 to-slate-900 -z-0" />
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    </div>
  );
};

export default VoIPSlide;
